import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { loadAllDeals } from 'app/state/ducks/deals/thunks'
import { Document } from 'app/models/document-params'
import { ContractType, DocumentStateTypes } from 'app/models/common-types'
import { loadAllFacilities } from 'app/state/ducks/facilities/thunks'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import { Progress, Stack, Text, FileInput, Flex } from '@mantine/core'
import SectionTitle from '../Headers&Text/SectionTitle'
import IonIcon from '@reacticons/ionicons'
import MainTable from '../Table/MainTable'
import { ColumnDefAttachmentsType, columnDefsAttachments } from './attachments-column-defs'
import { createDocument, deleteDocument, generatePresignedUrl, getDocumentFromS3, loadByContract, updateDocumentType } from 'app/state/ducks/documents/thunks'
import { getDocuments } from 'app/state/ducks/documents/selectors'
import axios from 'axios'
import { getDeals } from 'app/state/ducks/deals/selectors'
import { getFacilities } from 'app/state/ducks/facilities/selectors'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { DealParams } from 'app/models/deal-params'
import { FacilityParams } from 'app/models/facility-params'
import { EntityParams } from 'app/models/entity-params'
import DeleteConfirmation from '../delete-confirmation/DeleteConfirmation'
import { ErrorNotification, SuccessNotification } from '../notifications/notification'
import { validatePdfUpload } from 'app/utils/pdf-validation'
import { useForm } from '@mantine/form'
import { NewSelectInput } from '../inputs/selects/NewSelectInput'
import { dealsFacilitiesDocumentType, entitiesDocumentType, Option } from 'app/models/dropdown-options'
import { NewTextInput } from '../inputs/NewTextInput'
import UpdateTypeConfirmation from './update-type-confirmation'

export default function AttachmentsPage() {
    const params = useParams()
    const dispatch = useDispatch()

    const STAGE_LIMITS = {
        presignedMax: 20,
        uploadMax: 70,
        createLedgerRefernceMax: 80,
        scannedMax: 100,
    };


    const [contractType, setContractType] = useState<ContractType | null>(null)
    const [contractId, setContractId] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [progressValue, setProgressValue] = useState(0)
    const [uploadInProgress, setUploadInProgress] = useState(false)
    const [contract, setContract] = useState<DealParams | FacilityParams | EntityParams | undefined>(undefined)
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false)
    const [editTypeConfirmationVisible, setEditTypeConfirmationVisible] = useState(false)
    const [rowToDelete, setRowToDelete] = useState<any | null>(null);
    const [rowToUpdate, setRowToUpdate] = useState<any | null>(null);
    const [typeDropdownOptions, setTypeDropdownOptions] = useState<Option[]>([]);

    const allAttachments = useSelector(getDocuments)
    const [attachemnts, setAttachemnts] = useState<Document[]>([])

    const deals = useSelector(getDeals)
    const facilities = useSelector(getFacilities)
    const entities = useSelector(getEntities)


    useEffect(() => {
        if ('dealId' in params) {
            setContractType(ContractType.Deals)
            setContractId(params.dealId as string)
        } else if ('facilityId' in params) {
            setContractType(ContractType.Facilities)
            setContractId(params.facilityId as string)
        } else if ('entityId' in params) {
            setContractType(ContractType.Entities)
            setContractId(params.entityId as string)
        }
    }, [params])

    useEffect(() => {
        if (contractType === ContractType.Deals) {
            setContract(deals.find((deal: DealParams) => deal.id === contractId) as DealParams)
            setTypeDropdownOptions(dealsFacilitiesDocumentType)
            return
        }
        if (contractType === ContractType.Facilities) {
            setContract(facilities.find((facility: FacilityParams) => facility.id === contractId) as FacilityParams)
            setTypeDropdownOptions(dealsFacilitiesDocumentType)
            return
        }
        if (contractType === ContractType.Entities) {
            setContract(entities.find((entity: EntityParams) => entity.id === contractId) as EntityParams)
            setTypeDropdownOptions(entitiesDocumentType)
            return
        }
        setContract(undefined)
    }, [contractType, contractId, deals, facilities, entities])

    useEffect(() => {
        const fetchData = async () => {
            if (!contractType || !contractId) return

            setIsLoading(true)

            const promises = [
                contractType === ContractType.Deals && dispatch(loadAllDeals()),
                contractType === ContractType.Facilities && dispatch(loadAllFacilities()),
                contractType === ContractType.Entities && dispatch(loadEntities()),
                dispatch(loadByContract(contractId, contractType)),
            ].filter(Boolean) // Remove any falsey values (null/undefined/false)

            await Promise.all(promises)

            setIsLoading(false)
        }

        fetchData()
    }, [contractType, contractId, dispatch])

    useEffect(() => {
        if (!contractType || !contractId) return
        const documentStateType = DocumentStateTypes[contractType]
        setAttachemnts(allAttachments[documentStateType] ?? [])
    }, [contractType, contractId, allAttachments])


    const uploadForm = async (url: string, file: File) => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            await axios.put(url, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round(STAGE_LIMITS.presignedMax + (progressEvent.progress ?? 0.0) * (STAGE_LIMITS.uploadMax - STAGE_LIMITS.presignedMax));
                    setProgressValue(progress);
                },
            });
            return { success: true, payload: file };
        } catch (error: any) {
            console.error("Error uploading file to S3:", error);
            return { success: false, payload: error?.message };

        }
    };

    const form: any = useForm({
        initialValues: {
            id: undefined,
            name: '',
            mimeType: '',
            requestor: '',
            tags: '',
            signedBy: '',
            subTradeId: '',
            file: null,
            effectiveDate: null,
            documentType: '',
            documentTypeText: null
        },
        validate: {
            file: (value) => (value ? null : 'File is required'),
            documentType: (value) => (value ? null : 'Document type is required'),
            documentTypeText: (value, values) =>
                values.documentType === 'Other'
                    ? typeof value === 'string' && (value as string).trim() !== ''
                        ? null
                        : 'Other Type is required'
                    : null,
        }
    });

    const setFile = async (file: File | null) => {
        if (form.validate().hasErrors) return
        if (!file || !contractType || !contractId) return
        // Password-protected PDFs break packet/EML merging downstream (T-002942);
        // reject them before uploading (see app/utils/pdf-validation.ts).
        const pdfValidation = await validatePdfUpload(file)
        if (pdfValidation !== 'ok') {
            ErrorNotification({
                title: 'Document failed to upload',
                message: pdfValidation === 'password-protected'
                    ? 'This PDF is password-protected. Re-save it without protection (e.g. print to PDF) and upload again.'
                    : 'The selected file could not be read. It may have been moved or changed since it was selected — choose the file again.',
            })
            return
        }
        const contractKey = {
            id: contractId,
            admin: (contract as EntityParams)?.maker ?? (contract as DealParams)?.accountManagementAdmin ?? (contract as FacilityParams)?.accountManagementAdmin,
        }

        setUploadInProgress(true)
        setProgressValue(1);
        let presignProgress = 0;
        const presignInterval = setInterval(() => {
            presignProgress += 1;
            if (presignProgress <= STAGE_LIMITS.presignedMax - 1) {
                setProgressValue(presignProgress);
            }
        }, 50); // Adjust for desired speed


        const presignedResponse = await generatePresignedUrl(file.name, file.type)

        if (!presignedResponse || !presignedResponse.success) {
            ErrorNotification({
                title: 'Document failed to upload',
                message: presignedResponse?.payload ?? "Check data and try again"
            });

            setUploadInProgress(false)
            return
        }
        clearInterval(presignInterval)
        setProgressValue(STAGE_LIMITS.presignedMax)
        const { url, id } = presignedResponse.payload
        const presignedId = id
        const uploadResponse: any = await uploadForm(url, file)

        //THIS IS FOR ENTITY DOCUMENTS IN AADocMetaData
        if ((form.values.documentType === 'ADF' || form.values.documentType === 'TaxForm') && contractType === ContractType.Entities) {
            const presignedSPResponse = await generatePresignedUrl(file.name, file.type, 'signing', presignedId)
            const { url } = presignedSPResponse.payload
            await uploadForm(url, file)
        }

        if (!uploadResponse || !uploadResponse.success) {
            ErrorNotification({
                title: 'Document failed to upload',
                message: uploadResponse?.payload ?? "Check data and try again"
            });
            setUploadInProgress(false)
            return
        }
        setProgressValue(STAGE_LIMITS.uploadMax);
        let creationProgress = STAGE_LIMITS.uploadMax;
        const creationInterval = setInterval(() => {
            creationProgress += 1;
            if (creationProgress <= STAGE_LIMITS.createLedgerRefernceMax - 1) {
                setProgressValue(creationProgress);
            }
        }, 50); // Adjust for desired speed

        const newDocument = {
            id,
            fileName: file.name,
            fileType: file.type,
            contractId: contractKey,
            contractType: contractType,
            documentType: form.values.documentType,
            documentTypeText: form.values.documentTypeText,
        }

        const damlResponse: any = await dispatch(createDocument(newDocument))
        console.log('DAML response:', damlResponse);
        if (!damlResponse || !damlResponse.success) {
            ErrorNotification({
                title: 'Document failed to upload',
                message: damlResponse?.payload ?? "Check data and try again"
            });
            setUploadInProgress(false)
            return
        }
        clearInterval(creationInterval)
        setProgressValue(STAGE_LIMITS.createLedgerRefernceMax)


        let scanningProgress = STAGE_LIMITS.createLedgerRefernceMax;
        const scanningInterval = setInterval(() => {
            scanningProgress += 1;
            if (scanningProgress <= STAGE_LIMITS.scannedMax - 1
            ) {
                setProgressValue(scanningProgress);
            }
        }, 50); // Adjust for desired speed
        await new Promise(r => setTimeout(r, 1500)); // simulate delay
        clearInterval(scanningInterval)
        setProgressValue(STAGE_LIMITS.scannedMax)
        setUploadInProgress(false)
        SuccessNotification({ title: 'Document successfully uploaded' });
    }

    const deleteEntity = async (row: any) => {
        const result: any = await dispatch(deleteDocument(row.id))
        if (result && result.success) {
            SuccessNotification({ title: 'Document successfully deleted' });
            return
        }
        ErrorNotification({
            title: 'Document failed to delete',
            message: result?.payload ?? "Check data and try again"
        });
    }

    const handleDelete = async (row: any) => {
        setRowToDelete(row);
        setDeleteConfirmationVisible(true);
    };

    const handleEditType = async (otherDocumentTypeText?: string) => {
        setIsLoading(true)
        const result: any = await dispatch(updateDocumentType(rowToUpdate, otherDocumentTypeText))
        setIsLoading(false)
        setEditTypeConfirmationVisible(false);
        if (!result || !result.success) {
            ErrorNotification({
                title: 'Document failed to update',
                message: result?.payload ?? "Check data and try again"
            });
            return
        }
        SuccessNotification({ title: 'Document succesfuly updated' });
    }

    const handleOpen = async (row: any) => {
        const downloadResponse = await getDocumentFromS3(row.id)
        if (!downloadResponse || !downloadResponse.success) {
            ErrorNotification({
                title: 'Document failed to download',
                message: downloadResponse?.payload ?? "Check data and try again"
            });
            return
        }
        const { url } = downloadResponse.payload
        window.open(url, '_blank')
    };

    const handleDocumentTypeChange = (id: any, newValue: string) => {
        const newAttachments = [...attachemnts];
        const attachment = newAttachments.find((att) => att.id === id);
        if (!attachment) return;
        setRowToUpdate({ ...attachment, documentType: newValue });
        setEditTypeConfirmationVisible(true);
    }

    async function downloadDocument(row: any) {
        try {
            const downloadResponse = await getDocumentFromS3(row.id)
            if (!downloadResponse || !downloadResponse.success) {
                ErrorNotification({
                    title: 'Document failed to download',
                    message: downloadResponse?.payload ?? "Check data and try again"
                });
                return
            }
            const { url, document: docRef } = downloadResponse.payload

            const response = await axios.get(url, {
                responseType: 'blob', // Important: treat it as a file
            });

            const blob = new Blob([response.data]);
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = docRef.name;
            link.click();

            // Clean up
            window.URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error('Error downloading file:', err);
        }
    }

    const cleanData = (): ColumnDefAttachmentsType[] => {
        return attachemnts.map((attachment: Document) => {
            return {
                tags: attachment?.tags ? attachment.tags.join(', ') : "",
                documentType: attachment?.documentType,
                documentTypeText: attachment?.documentTypeText,
                contractType: contractType ?? '',
                uploadDate: attachment.createDate,
                uploader: "John Doe",
                attachments: attachment.name,
                id: attachment.id,
                onDelete: handleDelete,
                onDownload: downloadDocument,
                handleDocumentTypeChange,
            }
        }).sort((a, b) => {
            const dateA = new Date(a.uploadDate)
            const dateB = new Date(b.uploadDate)
            return dateB.getTime() - dateA.getTime()
        }
        )
    }


    return (
        <>
            <Stack w="100%" align='stretch'>
                <SectionTitle>Document Upload</SectionTitle>
                <Stack w='100%' className='sectionRemoveGap sectionBody' >
                    <Stack w='100%' pt='12px' className='sectionDetailBody' style={{ padding: '20px' }}>
                        <div className="attachments-container">
                            <div className="attachments-body">
                                <div className="document-overview-upload">
                                    <Flex gap="sm" align="flex-end" wrap="nowrap" w="100%">
                                        {/* Inputs container */}
                                        <div style={{ display: 'flex', flex: 1, gap: '12px' }}>
                                            {/* FileInput (takes 2x space) */}
                                            <div style={{ flex: 2 }}>
                                                <FileInput
                                                    classNames={{
                                                        rightSection: "customSellectRightSection",
                                                        input: "customSelectInput",
                                                        wrapper: "newInputWrapper",
                                                    }}
                                                    rightSection={
                                                        <IonIcon
                                                            style={{ width: "18px", height: "18px", top: "2px", left: "2px" }}
                                                            color="black"
                                                            name="search"
                                                        />
                                                    }
                                                    accept="application/pdf"
                                                    placeholder="Select File..."
                                                    {...form.getInputProps('file')}
                                                />
                                            </div>

                                            {/* NewSelectInput (smaller, but grows) */}
                                            <div style={{ flex: 1, minWidth: '250px', width: '100%' }}>
                                                <NewSelectInput
                                                    w="100%"
                                                    label="Type"
                                                    data={typeDropdownOptions}
                                                    placeholder="Type"
                                                    {...form.getInputProps('documentType')}
                                                />
                                            </div>

                                            {/* Optional NewTextInput (shown only for Other) */}
                                            {form.values.documentType === 'Other' && (
                                                <div style={{ flex: 1, minWidth: '150px', width: '100%' }}>
                                                    <NewTextInput
                                                        w="100%"
                                                        placeholder="Other Type"
                                                        {...form.getInputProps('documentTypeText')}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Button (fixed width) */}
                                        <div style={{ flexShrink: 0, marginBottom: "3px" }}>
                                            <button
                                                type="button"
                                                className="document-valivation-header-button-add-document"
                                                onClick={() => setFile(form.values.file)}
                                            >
                                                <div className="document-valivation-header-button-add-document-text">
                                                    Add Attachments
                                                </div>
                                            </button>
                                        </div>
                                    </Flex>


                                    {uploadInProgress && <div className='attachments-progress'>
                                        <Text className='attachments-progress-header'>Progress ...</Text>
                                        <Progress w="100%" value={progressValue} h="30px" label={`${progressValue}%`} size="xl" radius="xl" classNames={{
                                            root: "attachments-progress-root",
                                            label: "attachments-progress-label",
                                        }} />
                                    </div>}
                                </div>
                                <div style={{ width: '100%' }}>
                                    <MainTable
                                        tableName="Attachments Table"
                                        columnDefs={columnDefsAttachments}
                                        withPagination={true}
                                        data={cleanData()}
                                        isLoading={isLoading}
                                        enableTopToolbar={false}
                                        setRow={(row, column) => {
                                            if (column === "delete") return;
                                            handleOpen(row);
                                        }}
                                        headerBackgroundColor="#F0EEEE"
                                        enableColumnOrdering={false}
                                        enableColumnResizing={false}
                                        enableGrouping={false}
                                        enableColumnFilters={false}
                                        enableColumnActions={false}
                                        enableFilters={false}
                                        enableSorting={false}
                                        enableFullScreenToggle={false}
                                        enableDensityToggle={false}
                                        enableHiding={false}
                                        border={true}
                                        fromTradeEntry={false}
                                    />
                                </div>
                            </div>
                        </div>

                    </Stack>
                </Stack>
            </Stack >
            {deleteConfirmationVisible && (
                <DeleteConfirmation
                    onConfirm={() => {
                        if (!rowToDelete)
                            return
                        deleteEntity(rowToDelete);
                        setRowToDelete(null);
                        setDeleteConfirmationVisible(false);
                    }}
                    onCancel={() => {
                        setDeleteConfirmationVisible(false);
                    }}
                />
            )};
            {editTypeConfirmationVisible && (
                <UpdateTypeConfirmation
                    type={rowToUpdate?.documentType}
                    onConfirm={handleEditType}
                    onCancel={() => {
                        setEditTypeConfirmationVisible(false);
                    }}
                />
            )};
        </>
    )
}